/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Clock, TrendingUp, Target, BookOpen, Award, BarChart3, PieChart as PieChartIcon, Upload, FileText, Link as LinkIcon, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../ui/use-toast';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

/**
 * StudentGradebook Component
 *
 * Student-facing gradebook interface showing:
 * - Current grades and progress
 * - Assignment details and due dates
 * - Overall class performance
 * - Grade trends and feedback
 */
const StudentGradebook = ({ user }) => {
  const { toast } = useToast();

  // State management
  const [gradebookData, setGradebookData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCohort, setSelectedCohort] = useState('');
  const [cohorts, setCohorts] = useState([]);

  // Submission state
  const [submissions, setSubmissions] = useState({});  // { assignmentId: { file, uploading, submitted } }
  const [textSubmissions, setTextSubmissions] = useState({});  // { assignmentId: text }
  const [urlSubmissions, setUrlSubmissions] = useState({});  // { assignmentId: url }

  // Define functions first before useEffect
  const fetchStudentCohorts = useCallback(async () => {
    try {
      setLoading(true);
      // Get student's cohorts
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/student/cohorts`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch cohorts');

      const data = await response.json();
      setCohorts(data);

      // Auto-select first cohort if available
      if (data.length > 0) {
        setSelectedCohort(data[0].id.toString());
      }
    } catch (error) {
      console.error('Failed to fetch student cohorts:', error);
      toast({
        title: "Error",
        description: "Failed to load your classes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchStudentGrades = useCallback(async () => {
    if (!selectedCohort) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/gradebook/student/cohort/${selectedCohort}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch grades');

      const data = await response.json();
      console.log('📊 Student gradebook data received:', {
        cohort: data.cohort,
        gradesCount: data.grades?.length,
        settings: data.settings,
        sampleGrade: data.grades?.[0]
      });
      setGradebookData(data);
    } catch (error) {
      console.error('Failed to fetch student grades:', error);
      toast({
        title: "Error",
        description: "Failed to load your grades",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [selectedCohort, toast]);

  // Get grade calculation from backend (single source of truth)
  // Backend now performs the calculation to ensure consistency with teacher view
  const calculateOverallGrade = () => {
    // Use backend-calculated grade if available (new system)
    if (gradebookData?.calculatedGrade) {
      console.log('✅ Using backend-calculated grade:', gradebookData.calculatedGrade);
      return {
        finalGrade: gradebookData.calculatedGrade.finalGrade,
        letterGrade: gradebookData.calculatedGrade.letterGrade,
        categoryBreakdown: gradebookData.calculatedGrade.categoryBreakdown || {}
      };
    }

    // Fallback for backwards compatibility (shouldn't happen after deployment)
    console.warn('⚠️ Backend grade calculation not available, using fallback');
    return {
      finalGrade: 0,
      letterGrade: 'I',
      categoryBreakdown: {}
    };
  };

  const getRecentGrades = () => {
    if (!gradebookData?.grades) return [];

    return gradebookData.grades
      .filter(grade =>
        !grade.is_hidden_from_students && // Hide hidden assignments
        grade.is_released !== false && // Only show released grades (allow null/undefined for backwards compatibility)
        grade.percentage !== null &&
        grade.graded_at
      )
      .sort((a, b) => new Date(b.graded_at) - new Date(a.graded_at))
      .slice(0, 5);
  };

  // Get assignments that allow submissions and haven't been graded yet
  const getSubmissionAssignments = () => {
    if (!gradebookData?.grades) return [];

    return gradebookData.grades
      .filter(grade =>
        grade.allows_submissions &&  // Assignment accepts submissions
        !grade.is_hidden_from_students &&  // Not hidden
        grade.percentage === null  // Not yet graded
      )
      .sort((a, b) => {
        // Sort by due date (soonest first), then by name
        if (a.due_date && b.due_date) {
          return new Date(a.due_date) - new Date(b.due_date);
        }
        return (a.assignment_name || '').localeCompare(b.assignment_name || '');
      });
  };

  // Handle file selection
  const handleFileSelect = (assignmentId, event) => {
    const file = event.target.files[0];
    if (file) {
      setSubmissions(prev => ({
        ...prev,
        [assignmentId]: { file, uploading: false, submitted: false }
      }));
    }
  };

  // Handle file submission
  const handleFileSubmission = async (assignmentId) => {
    const submission = submissions[assignmentId];
    if (!submission?.file) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmissions(prev => ({
        ...prev,
        [assignmentId]: { ...prev[assignmentId], uploading: true }
      }));

      const formData = new FormData();
      formData.append('file', submission.file);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/gradebook/assignments/${assignmentId}/submit`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to submit');
      }

      const result = await response.json();

      setSubmissions(prev => ({
        ...prev,
        [assignmentId]: { ...prev[assignmentId], uploading: false, submitted: true }
      }));

      toast({
        title: "✅ Submitted!",
        description: result.isLate ? "Submission received (late)" : "Submission received successfully"
      });

      // Refresh gradebook data
      fetchStudentGrades();

    } catch (error) {
      console.error('Submission error:', error);
      setSubmissions(prev => ({
        ...prev,
        [assignmentId]: { ...prev[assignmentId], uploading: false }
      }));
      toast({
        title: "Submission failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Handle text submission
  const handleTextSubmission = async (assignmentId) => {
    const text = textSubmissions[assignmentId];
    if (!text?.trim()) {
      toast({
        title: "Empty submission",
        description: "Please enter some text",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/gradebook/assignments/${assignmentId}/submit`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ submissionType: 'text', textContent: text })
        }
      );

      if (!response.ok) throw new Error('Failed to submit');

      toast({
        title: "✅ Submitted!",
        description: "Text submission received"
      });

      setTextSubmissions(prev => ({ ...prev, [assignmentId]: '' }));
      fetchStudentGrades();

    } catch (error) {
      toast({
        title: "Submission failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Handle URL submission
  const handleUrlSubmission = async (assignmentId) => {
    const url = urlSubmissions[assignmentId];
    if (!url?.trim()) {
      toast({
        title: "Empty URL",
        description: "Please enter a URL",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/gradebook/assignments/${assignmentId}/submit`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ submissionType: 'url', urlContent: url })
        }
      );

      if (!response.ok) throw new Error('Failed to submit');

      toast({
        title: "✅ Submitted!",
        description: "URL submission received"
      });

      setUrlSubmissions(prev => ({ ...prev, [assignmentId]: '' }));
      fetchStudentGrades();

    } catch (error) {
      toast({
        title: "Submission failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Load student's cohorts and grades on mount
  useEffect(() => {
    fetchStudentCohorts();
  }, [fetchStudentCohorts]);

  useEffect(() => {
    if (selectedCohort) {
      fetchStudentGrades();
    }
  }, [selectedCohort, fetchStudentGrades]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading your grades...</p>
        </div>
      </div>
    );
  }

  if (!selectedCohort || !gradebookData) {
    return (
      <div className="text-center p-8">
        <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-600 mb-2">No Grades Available</h3>
        <p className="text-gray-500">Your grades will appear here once assignments are graded.</p>
      </div>
    );
  }

  const { finalGrade, letterGrade, categoryBreakdown } = calculateOverallGrade();
  const recentGrades = getRecentGrades();

  // Category colors matching teacher view
  const categoryColors = {
    'Safety': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
    'Projects': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
    'Classwork': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
    'default': { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' }
  };

  // Helper to get status badge
  const getStatusBadge = (grade) => {
    const badges = [];

    if (grade.status === 'nhi') {
      badges.push(<Badge key="nhi" variant="secondary" className="text-xs bg-gray-300">NHI</Badge>);
    } else if (grade.status === 'exc') {
      badges.push(<Badge key="exc" variant="secondary" className="text-xs bg-purple-100 text-purple-700">EXC</Badge>);
    } else if (grade.percentage !== null) {
      badges.push(
        <Badge key="graded" variant={grade.percentage >= 70 ? "default" : "destructive"} className="text-xs">
          Graded
        </Badge>
      );
    } else {
      badges.push(<Badge key="pending" variant="secondary" className="text-xs">Pending</Badge>);
    }

    if (grade.is_late) {
      badges.push(<Badge key="late" variant="destructive" className="text-xs">Late</Badge>);
    }
    if (grade.is_excused) {
      badges.push(<Badge key="excused" variant="outline" className="text-xs">Excused</Badge>);
    }
    if (grade.auto_synced) {
      badges.push(<Badge key="auto" variant="outline" className="text-xs bg-blue-50 text-blue-600">Auto</Badge>);
    }

    return badges;
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-4">
        <h1 className="text-3xl font-bold text-gray-900">My Grades</h1>
      </div>

      {/* Class Tabs */}
      {cohorts.length > 0 ? (
        <Tabs value={selectedCohort} onValueChange={setSelectedCohort} className="w-full">
          <TabsList className="w-full justify-start mb-6 bg-gray-100">
            {cohorts.map(cohort => (
              <TabsTrigger
                key={cohort.id}
                value={cohort.id.toString()}
                className="flex items-center gap-2"
              >
                <span>{cohort.name}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {cohorts.map(cohort => (
            <TabsContent key={cohort.id} value={cohort.id.toString()}>

              {/* Sub-tabs for different views */}
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="all-assignments">All Assignments</TabsTrigger>
                  <TabsTrigger value="progress">Progress</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-6">
              {/* Overall Grade Card */}
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 mb-6">
                <CardHeader className="text-center">
                  <CardTitle className="flex items-center justify-center gap-2">
                    <Award className="h-6 w-6 text-blue-600" />
                    {cohort.name} - Overall Grade
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-4">
                    <div className="text-5xl font-bold text-blue-700 mb-2">{finalGrade}%</div>
                    <Badge
                      variant={finalGrade >= 70 ? "default" : "destructive"}
                      className="text-lg px-4 py-1"
                    >
                      {letterGrade}
                    </Badge>
                  </div>
                  <Progress value={finalGrade} className="w-full max-w-md mx-auto h-3" />
                  <p className="text-sm text-gray-600 mt-2">
                    {finalGrade >= 70 ? "Great work! Keep it up!" : "You can improve - don't give up!"}
                  </p>
                </CardContent>
              </Card>

      {/* Category Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(categoryBreakdown).map(([category, data]) => (
          <Card key={category}>
            <CardContent className="p-4">
              <div className="text-center">
                <h3 className="font-semibold text-gray-700 mb-2">{category}</h3>
                <div className="text-2xl font-bold text-blue-600 mb-1">{data.average}%</div>
                <div className="text-xs text-gray-500">
                  {data.count} assignment{data.count !== 1 ? 's' : ''} • {data.weight}% weight
                </div>
                <Progress value={data.average} className="mt-2 h-2" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Grades */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Recent Grades
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentGrades.length > 0 ? (
                recentGrades.map((grade, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">{grade.assignment_name}</div>
                      <div className="text-sm text-gray-500">
                        {grade.category_name} • {new Date(grade.graded_at).toLocaleDateString()}
                      </div>
                      {grade.teacher_notes && (
                        <div className="text-xs text-gray-600 mt-1 italic">{grade.teacher_notes}</div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${grade.percentage >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                        {Math.round(grade.percentage)}%
                      </div>
                      {grade.is_late && (
                        <Badge variant="destructive" className="text-xs mt-1">Late</Badge>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No recent grades available</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Assignment Submissions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Submit Work
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Upload files, text, or links for assignments
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {getSubmissionAssignments().length > 0 ? (
                getSubmissionAssignments().map((assignment) => {
                  const assignmentId = assignment.assignment_id;
                  const submissionState = submissions[assignmentId];

                  return (
                    <div key={assignmentId} className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="font-medium text-gray-900">{assignment.assignment_name}</div>
                          <div className="text-sm text-gray-600">{assignment.category_name} • {assignment.points_possible} points</div>
                          {assignment.description && (
                            <div className="text-xs text-gray-500 mt-1">{assignment.description}</div>
                          )}
                        </div>
                        {assignment.due_date && (
                          <div className="text-right">
                            <div className="flex items-center text-orange-600 text-sm">
                              <Clock className="h-4 w-4 mr-1" />
                              Due {new Date(assignment.due_date).toLocaleDateString()}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Submission Forms */}
                      <div className="space-y-3 mt-3">
                        {/* File Upload */}
                        {assignment.submission_types?.includes('file') && (
                          <div className="bg-white p-3 rounded border">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="h-4 w-4 text-gray-600" />
                              <label className="text-sm font-medium">Upload File</label>
                            </div>
                            <div className="flex gap-2">
                              <input
                                type="file"
                                onChange={(e) => handleFileSelect(assignmentId, e)}
                                className="text-sm flex-1"
                                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.zip"
                              />
                              <Button
                                onClick={() => handleFileSubmission(assignmentId)}
                                disabled={!submissionState?.file || submissionState?.uploading}
                                size="sm"
                              >
                                {submissionState?.uploading ? 'Uploading...' : 'Submit'}
                              </Button>
                            </div>
                            {submissionState?.file && (
                              <div className="text-xs text-gray-600 mt-1">
                                Selected: {submissionState.file.name} ({Math.round(submissionState.file.size / 1024)} KB)
                              </div>
                            )}
                            {submissionState?.submitted && (
                              <div className="flex items-center gap-1 text-xs text-green-600 mt-2">
                                <CheckCircle className="h-3 w-3" />
                                Submitted successfully!
                              </div>
                            )}
                          </div>
                        )}

                        {/* Text Submission */}
                        {assignment.submission_types?.includes('text') && (
                          <div className="bg-white p-3 rounded border">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="h-4 w-4 text-gray-600" />
                              <label className="text-sm font-medium">Text Submission</label>
                            </div>
                            <textarea
                              value={textSubmissions[assignmentId] || ''}
                              onChange={(e) => setTextSubmissions(prev => ({
                                ...prev,
                                [assignmentId]: e.target.value
                              }))}
                              placeholder="Enter your text submission here..."
                              className="w-full p-2 border rounded text-sm min-h-[80px]"
                            />
                            <Button
                              onClick={() => handleTextSubmission(assignmentId)}
                              disabled={!textSubmissions[assignmentId]?.trim()}
                              size="sm"
                              className="mt-2"
                            >
                              Submit Text
                            </Button>
                          </div>
                        )}

                        {/* URL Submission */}
                        {assignment.submission_types?.includes('url') && (
                          <div className="bg-white p-3 rounded border">
                            <div className="flex items-center gap-2 mb-2">
                              <LinkIcon className="h-4 w-4 text-gray-600" />
                              <label className="text-sm font-medium">URL/Link Submission</label>
                            </div>
                            <div className="flex gap-2">
                              <input
                                type="url"
                                value={urlSubmissions[assignmentId] || ''}
                                onChange={(e) => setUrlSubmissions(prev => ({
                                  ...prev,
                                  [assignmentId]: e.target.value
                                }))}
                                placeholder="https://..."
                                className="flex-1 p-2 border rounded text-sm"
                              />
                              <Button
                                onClick={() => handleUrlSubmission(assignmentId)}
                                disabled={!urlSubmissions[assignmentId]?.trim()}
                                size="sm"
                              >
                                Submit URL
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Upload className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No assignments accepting submissions at this time</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
                </TabsContent>

                {/* All Assignments Tab */}
                <TabsContent value="all-assignments">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            All Assignments
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-1.5 px-3 font-semibold">Assignment</th>
                  <th className="text-left py-1.5 px-3 font-semibold">Category</th>
                  <th className="text-center py-1.5 px-3 font-semibold">Due Date</th>
                  <th className="text-center py-1.5 px-3 font-semibold">Points</th>
                  <th className="text-center py-1.5 px-3 font-semibold">Grade</th>
                  <th className="text-center py-1.5 px-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {gradebookData.grades
                  .filter(grade =>
                    !grade.is_hidden_from_students && // Hide hidden assignments from display
                    grade.is_released !== false // Only show released grades (allow null/undefined for backwards compatibility)
                  )
                  .map((grade, index) => {
                  const colors = categoryColors[grade.category_name] || categoryColors.default;
                  return (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-1.5 px-3">
                      <div>
                        <div className="font-medium" title={grade.description || ''}>
                          {grade.assignment_name}
                        </div>
                        {grade.teacher_notes && (
                          <div className="text-xs text-blue-600 italic" title={`Teacher note: ${grade.teacher_notes}`}>
                            💬 {grade.teacher_notes}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-1.5 px-3">
                      <Badge variant="outline" className={`text-xs ${colors.bg} ${colors.text} border-0`}>
                        {grade.category_name}
                      </Badge>
                    </td>
                    <td className="py-1.5 px-3 text-center">
                      {grade.due_date ? new Date(grade.due_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-1.5 px-3 text-center">
                      {grade.points_earned !== null ?
                        `${Math.round(grade.points_earned)}/${grade.points_possible}` :
                        `—/${grade.points_possible}`
                      }
                    </td>
                    <td className="py-1.5 px-3 text-center">
                      {grade.percentage !== null ? (
                        <span className={`font-bold ${grade.percentage >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                          {Math.round(grade.percentage)}%
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-1.5 px-3 text-center">
                      <div className="flex justify-center gap-1 flex-wrap">
                        {getStatusBadge(grade)}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
                </TabsContent>

                {/* Progress Tab - NEW */}
                <TabsContent value="progress">
                  <div className="space-y-6">
                    {/* Stats Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <Award className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                            <div className="text-2xl font-bold text-blue-600">{finalGrade}%</div>
                            <div className="text-sm text-gray-600">Overall Grade</div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <Target className="h-8 w-8 text-green-600 mx-auto mb-2" />
                            <div className="text-2xl font-bold text-green-600">
                              {gradebookData.grades.filter(g => g.percentage !== null && !g.is_hidden_from_students).length}
                            </div>
                            <div className="text-sm text-gray-600">Completed</div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <Clock className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                            <div className="text-2xl font-bold text-orange-600">
                              {gradebookData.grades.filter(g => g.percentage === null && !g.is_hidden_from_students).length}
                            </div>
                            <div className="text-sm text-gray-600">Pending</div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <TrendingUp className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                            <div className="text-2xl font-bold text-purple-600">
                              {Object.keys(categoryBreakdown).length}
                            </div>
                            <div className="text-sm text-gray-600">Categories</div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Category Performance Pie Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <PieChartIcon className="h-5 w-5" />
                          Category Performance
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={Object.entries(categoryBreakdown).map(([name, data]) => ({
                                  name,
                                  value: data.average,
                                  weight: data.weight,
                                  count: data.count
                                }))}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, value }) => `${name}: ${value}%`}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {Object.entries(categoryBreakdown).map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'][index % 5]} />
                                ))}
                              </Pie>
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                      <div className="bg-white p-3 shadow-lg rounded-lg border">
                                        <p className="font-semibold">{data.name}</p>
                                        <p className="text-sm">Average: {data.value}%</p>
                                        <p className="text-sm">Weight: {data.weight}%</p>
                                        <p className="text-sm">Assignments: {data.count}</p>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Grade Timeline - All Assignments */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <BarChart3 className="h-5 w-5" />
                          All Assignments Timeline
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <div style={{ minWidth: `${Math.max(800, gradebookData.grades.filter(g => !g.is_hidden_from_students && g.is_released !== false).length * 60)}px`, height: '400px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={gradebookData.grades
                                  .filter(g => !g.is_hidden_from_students && g.is_released !== false)
                                  .sort((a, b) => {
                                    // Sort by graded_at if available, otherwise by created_at or assignment order
                                    const dateA = a.graded_at ? new Date(a.graded_at) : new Date(a.created_at || 0);
                                    const dateB = b.graded_at ? new Date(b.graded_at) : new Date(b.created_at || 0);
                                    return dateA - dateB;
                                  })
                                  .map((g, index) => ({
                                    name: g.assignment_name.length > 15 ? g.assignment_name.substring(0, 15) + '...' : g.assignment_name,
                                    fullName: g.assignment_name,
                                    grade: g.percentage !== null ? Math.round(g.percentage) : null,
                                    date: g.graded_at ? new Date(g.graded_at).toLocaleDateString() : 'Not graded',
                                    category: g.category_name,
                                    status: g.percentage !== null ? 'Graded' : 'Pending',
                                    index: index
                                  }))}
                                margin={{ top: 5, right: 30, left: 20, bottom: 80 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="name"
                                  angle={-45}
                                  textAnchor="end"
                                  height={100}
                                  interval={0}
                                />
                                <YAxis domain={[0, 100]} label={{ value: 'Grade %', angle: -90, position: 'insideLeft' }} />
                                <Tooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      const data = payload[0].payload;
                                      return (
                                        <div className="bg-white p-3 shadow-lg rounded-lg border">
                                          <p className="font-semibold">{data.fullName}</p>
                                          <p className="text-sm">Status: {data.status}</p>
                                          {data.grade !== null && <p className="text-sm">Grade: {data.grade}%</p>}
                                          <p className="text-sm">Category: {data.category}</p>
                                          <p className="text-sm">Date: {data.date}</p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Legend />
                                <Line
                                  type="monotone"
                                  dataKey="grade"
                                  stroke="#3b82f6"
                                  strokeWidth={2}
                                  name="Grade %"
                                  connectNulls={false}
                                  dot={{ fill: '#3b82f6', r: 4 }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey={() => 70}
                                  stroke="#10b981"
                                  strokeDasharray="5 5"
                                  name="Passing (70%)"
                                  strokeWidth={1}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 mt-2 text-center">
                          ← Scroll horizontally to see all assignments →
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

              </Tabs>
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <div className="text-center p-8">
          <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No Classes Found</h3>
          <p className="text-gray-500">You are not enrolled in any classes yet.</p>
        </div>
      )}
    </div>
  );
};

export default StudentGradebook;